import Button from '@/components/atoms/Button/ButtonV2'
import React, { FC, ReactNode } from 'react'

interface CarouselItem {
  title?: string
  subtitle?: string
  color: string
  link: string
  logo?: ReactNode | string
  backgroundImage?: string
}

export interface CarouselProps {
  title: string
  subtitle?: string
  items: CarouselItem[]
  viewAllLabel?: string
  viewAllLink: string
}

const Carousel: FC<CarouselProps> = ({
  title,
  subtitle = '',
  items,
  viewAllLabel = 'View All',
  viewAllLink
}) => {
  return (
    <div className="flex flex-col items-start gap-6">
      <div className="flex w-full items-center justify-between">
        <div>
          <h2 className="max-w-[300px] font-FieldsVariable text-2xl font-medium text-gray-1100 md:max-w-[400px] xl:max-w-none">
            {title}
          </h2>
          {subtitle && <h3 className="text-gray-700">{subtitle}</h3>}
        </div>
        <Button
          type="tertiary"
          className="hidden ease-in-out md:block"
          as="a"
          href={viewAllLink}
        >
          {viewAllLabel}
        </Button>
      </div>
      <div className="-mx-4 w-screen md:-mx-6 lg:-mx-8 xl:mx-0 xl:w-full">
        <div className="hide-scroll flex w-full items-start gap-6 overflow-x-scroll px-4 xs:gap-4 sm:gap-6 md:px-6 lg:px-8 xl:px-0">
          {items.map((item, index) => (
            <a
              href={item.link}
              key={`${index}_${item.title}`}
              className="group flex w-full flex-col gap-3"
            >
              <div className="relative">
                <div
                  className={`flex aspect-[3/2] min-h-[124px] w-[59vw] items-center justify-center rounded-xl xs:w-[39vw] md:w-auto md:min-w-[166px] xl:h-[149px] ${
                    item.backgroundImage ? 'bg-cover' : item.color
                  }`}
                  style={
                    item.backgroundImage
                      ? { backgroundImage: `url(${item.backgroundImage})` }
                      : {}
                  }
                >
                  {item.logo &&
                    (typeof item.logo === 'string' ? (
                      <img
                        src={item.logo}
                        alt={item.title}
                        className="mx-auto size-12"
                      />
                    ) : (
                      React.cloneElement(item.logo as React.ReactElement)
                    ))}
                </div>
                {/* overlay */}
                <div
                  className={`pointer-events-none absolute inset-0 rounded-xl bg-black/0 transition-colors ${
                    item.backgroundImage
                      ? 'group-hover:bg-black/20 group-active:bg-black/30'
                      : 'group-hover:bg-black/[0.03] group-active:bg-black/[0.06]'
                  }`}
                ></div>
              </div>
              <div className="flex flex-col gap-2">
                {item.title && (
                  <h4 className="text-[20px] font-medium leading-8 text-gray-1100">
                    {item.title}
                  </h4>
                )}
                {item.subtitle && (
                  <p className="text-sm text-gray-700">{item.subtitle}</p>
                )}
              </div>
            </a>
          ))}
        </div>
      </div>
      <Button
        type="tertiary"
        className="w-full ease-in-out md:hidden"
        as="a"
        href={viewAllLink}
      >
        {viewAllLabel}
      </Button>
    </div>
  )
}

export default Carousel
